import { z } from "zod";
import {
  timestampToDate,
  timestampToDateNullable,
  serverStampDate,
} from "@/core/firebase/converters";
import { lenient, lenientNullable } from "@/core/zod/lenient";

/**
 * Booking document. Stored at `clubs/{clubId}/bookings/{bookingId}`.
 * See model-change-checklist.md before adding or renaming fields.
 */

// BookingStatus.noShow serialises to 'no_show', checkedIn to 'checked_in'.
export const BOOKING_STATUS = [
  "confirmed",
  "cancelled",
  "no_show",
  "checked_in",
] as const;
export const BookingStatusSchema = z.enum(BOOKING_STATUS);
export type BookingStatus = z.infer<typeof BookingStatusSchema>;

export const BOOKING_STATUS_LABELS: Record<BookingStatus, string> = {
  confirmed: "Confirmada",
  cancelled: "Cancelada",
  no_show: "No presentado",
  checked_in: "Presentado",
};

/**
 * Single source of truth for which statuses FREE the court slot — i.e. a
 * booking in one of these states does NOT block a new/moved/extended booking
 * on the same court+time. Cancelled (the slot was given back) and no_show (the
 * player never came, so the court is available) both free it.
 *
 * EVERY overlap / availability check MUST route through this — the server
 * guard (`BookingsRepository.assertNoOverlap`), the pure conflict finder
 * (`schedule-conflicts.findScheduleConflict`), the drag-move preview
 * (`use-booking-drag.checkOverlap`), and the resize clamp
 * (`resize-bounds.computeResizeBounds`). Keeping the set in one place is what
 * stops the four sites from drifting (which silently broke
 * rebook-/drag-/resize-onto-a-no_show-freed slot).
 *
 * MOVED to `@upadelmatch/core` on 2026-08-13 and re-exported here so the ~4
 * existing call sites keep their import path. Two new consumers made this
 * app-local home wrong: usersmatch renders the same slot grid, and Phase 2's
 * authoritative overlap check runs in a Python Cloud Function. The Python side
 * cannot import a TS package, so it mirrors the rule in
 * `functions/booking_slot.py` and `scripts/check-freeing-status-parity.mjs`
 * fails the build if the two sets ever disagree.
 */
export { isFreeingStatus } from "@upadelmatch/core";

/**
 * Legal status transitions. Self-transitions are always allowed (idempotent
 * writes). See `docs/booking-cancellation-lifecycle.md` for the rationale
 * behind each edge.
 */
const BOOKING_STATUS_TRANSITIONS: Record<
  BookingStatus,
  readonly BookingStatus[]
> = {
  confirmed: ["cancelled", "no_show", "checked_in"],
  checked_in: ["confirmed", "no_show", "cancelled"],
  // `no_show → cancelled` added 2026-05-15 (audit D2): a prepaid no-show
  // that the club decides to cancel + refund as goodwill / dispute
  // resolution previously needed a 2-hop no_show→confirmed→cancelled
  // detour. Direct edge pairs with the refund-at-cancel prompt.
  no_show: ["confirmed", "checked_in", "cancelled"],
  // Cancelled is recoverable — the undo-toast flow in
  // `useCancelBookingWithUndo` + the role-gated "Restaurar" action bring the
  // booking back to life. See docs/booking-cancellation-lifecycle.md §4.
  cancelled: ["confirmed"],
};

export function canTransitionBookingStatus(
  from: BookingStatus,
  to: BookingStatus,
): boolean {
  if (from === to) return true;
  return BOOKING_STATUS_TRANSITIONS[from].includes(to);
}

/**
 * Mermaid source rendering `BOOKING_STATUS_TRANSITIONS` as a state diagram.
 * Lives here so any change to the transition map shows up in the same diff
 * as the diagram update. Consumed by the Help panel on Reservas / Hoy /
 * Calendario via `help-registry.ts`.
 *
 * Node colors match the tag tones from `shared/utils/booking-ui.ts#statusToTone`
 * so the diagram reads as a legend for the booking tile colors:
 *   confirmed → info (blue / accent)
 *   checked_in → ok (green)
 *   no_show → muted (grey)
 *   cancelled → bad (red)
 */
export const BOOKING_STATE_DIAGRAM = `
flowchart LR
  start(( )) -->|crear| confirmed
  confirmed <-->|presentado| checked_in
  confirmed <-->|no presentado| no_show
  confirmed -->|cancelar| cancelled
  cancelled -->|restaurar| confirmed
  checked_in <-->|sí vino| no_show
  checked_in -->|cancelar| cancelled
  no_show -->|cancelar| cancelled

  classDef confirmed fill:#143a5c,stroke:#5db3ff,color:#d9ecff,stroke-width:2px
  classDef checked_in fill:#1e4026,stroke:#3cb371,color:#d4ffe4,stroke-width:2px
  classDef no_show fill:#2a2d31,stroke:#777,color:#ccc,stroke-width:2px
  classDef cancelled fill:#4a1e1e,stroke:#e06262,color:#ffd4d4,stroke-width:2px
  classDef start fill:#fff,stroke:#fff,color:#fff
  class confirmed confirmed
  class checked_in checked_in
  class no_show no_show
  class cancelled cancelled
  class start start
`.trim();

// ─── Payment status (derived state) ────────────────────────────────────────
//
// The booking carries a payment *state* (derived from the payments log and
// the free flag) and a list of payment events. Each event records its own
// method, so a single booking can mix cash + card + transfer. Status is
// stored (not just derived) so Firestore queries can filter on it directly.

// `refunded` is LEGACY: no longer derived (a fully-refunded booking reads
// `pending` + a ↩ marker — see `derivePaymentStatus`). Retained only so
// already-stored docs parse; remove on the next dev wipe+reseed.
export const PAYMENT_STATUS = [
  "pending",
  "partial",
  "paid",
  "free",
  "refunded",
] as const;
export const PaymentStatusSchema = z.enum(PAYMENT_STATUS);
export type PaymentStatus = z.infer<typeof PaymentStatusSchema>;

export const PAYMENT_STATUS_LABELS: Record<PaymentStatus, string> = {
  pending: "Pendiente",
  partial: "Parcial",
  paid: "Pagada",
  free: "Gratuita",
  refunded: "Reembolsada",
};

// ─── Payment method (how a single payment was collected) ───────────────────

export const PAYMENT_METHOD = ["cash", "card", "bizum", "transfer", "other"] as const;
export const PaymentMethodSchema = z.enum(PAYMENT_METHOD);
export type PaymentMethod = z.infer<typeof PaymentMethodSchema>;

// Legacy values that existed in historical bookings before the enum was
// frozen. Coerced silently to their current equivalent BEFORE the lenient
// wrapper sees them (see PaymentSchema below) so the drift log + Sentry
// capture only fires on truly unexpected values. Sentry CLUBMANAGER-PWA-36 /
// 35 / 34 / 33 closed by this map — `wallet` was emitting ~92 events/release
// because every demo booking with a wallet-refund triggered the drift log.
const LEGACY_PAYMENT_METHOD_MAP: Record<string, PaymentMethod> = {
  wallet: "other",
};

export const PAYMENT_METHOD_LABELS: Record<PaymentMethod, string> = {
  cash: "Efectivo",
  card: "Tarjeta",
  bizum: "Bizum",
  transfer: "Transferencia",
  other: "Otro",
};

// ─── Payment event ─────────────────────────────────────────────────────────
//
// One payment = one collection action. Positive `amount` for collection,
// negative for refund. Stored inline on the booking (max realistic count is
// ~5: one per player + occasional corrections).

/**
 * How a refund was returned to the customer. Optional metadata on
 * negative-amount entries — the `method` field already says how the
 * money flowed (cash/card/transfer); `refundType` documents the *form*
 * of the refund:
 *
 * - `card`         — refunded back to the original card (Stripe refund
 *                    or POS card-back).
 * - `cash`         — operator returned cash from the till.
 * - `wallet`       — credited the player's wallet balance.
 * - `credit_note`  — issued a credit note for future bookings.
 *
 * Null on positive-amount entries.
 */
export const PAYMENT_REFUND_TYPE = ["card", "cash", "wallet", "credit_note"] as const;
export const PaymentRefundTypeSchema = z.enum(PAYMENT_REFUND_TYPE);
export type PaymentRefundType = z.infer<typeof PaymentRefundTypeSchema>;

/**
 * Where a payment came FROM — orthogonal to `method` (how the money moved).
 * `method` is cash/card/bizum/transfer; `source` is the system/channel that
 * tendered it: the counter (`pos`), an online checkout (`online`), a prepaid
 * pack (`bono`), the player wallet (`wallet`), a booking deposit (`deposit`),
 * or a gift card. Future tenders add a value here + an emitter — no schema
 * shape change. Lenient → 'other' for unknowns; defaults to `pos`.
 */
export const PAYMENT_SOURCE = [
  "pos",
  "online",
  "bono",
  "wallet",
  "deposit",
  "gift_card",
  "other",
] as const;
export const PaymentSourceSchema = z.enum(PAYMENT_SOURCE);
export type PaymentSource = z.infer<typeof PaymentSourceSchema>;

export const PaymentSchema = z.object({
  id: z.string(),
  /** Positive for collection, negative for a refund. */
  amount: z.number(),
  // Lenient: unknown enum value (legacy data, typo'd seeder, future
  // schema drift) coerces to 'other' so the booking still renders.
  // Drift is logged via the `lenient()` helper so Sentry still hears
  // about it. Forms + callable inputs that need strict validation
  // should import `PaymentMethodSchema` directly. See
  // `src/core/firebase/repository.ts` for the parse pipeline this protects.
  // Preprocess silently maps known historical values (e.g. `wallet`) to
  // their current equivalent BEFORE lenient sees them — those are
  // expected legacy data, not drift, so they shouldn't fire a Sentry
  // warning every parse. Truly unknown values still hit lenient.
  method: z.preprocess(
    (v) => (typeof v === 'string' && v in LEGACY_PAYMENT_METHOD_MAP ? LEGACY_PAYMENT_METHOD_MAP[v] : v),
    lenient(PaymentMethodSchema, 'other', 'payment.method'),
  ),
  /** Refund classification — set on negative-amount entries; null otherwise. */
  refundType: lenientNullable(PaymentRefundTypeSchema, 'payment.refundType'),
  /** Channel the money came through (counter / online / bono / wallet / …).
   *  Orthogonal to `method`. Optional/additive — absent reads as `pos`
   *  (operator-recorded at the counter). */
  source: lenient(PaymentSourceSchema, 'other', 'payment.source').optional(),
  paidAt: timestampToDate,
  /** Which player in the roster paid, when splitting across players. */
  playerIndex: z.number().int().nullable().optional(),
  /** Audit — who recorded the payment. */
  collectedByUid: z.string().nullable().optional(),
  /** Optional operator note on the payment. */
  note: z.string().nullable().optional(),
  /**
   * For refund entries (negative `amount`), optional ref to the
   * `lineItems[].id` being refunded. Booking lineItems are immutable
   * by design; this is audit metadata only.
   */
  refundLineItemId: z.string().nullable().optional(),
});
export type Payment = z.infer<typeof PaymentSchema>;

// ─── Booking line items (what's owed — priced lines of the booking) ────────
//
// Each booking decomposes its price into an ordered list of line items. The
// court fee is always present (exactly one `court_fee` line); surcharges
// (lights, heating, guest fee), add-ons (ball rental, coach fee), and
// credits/discounts (discount, bono_credit, wallet_credit) are optional.
//
// Tax contract (clarified 2026-05-24): club-to-player prices are TAX-
// INCLUSIVE end-to-end. The `unitPrice` on every line is the final
// amount the player pays — no IVA added on top. This mirrors the sales
// model (sale.ts) + matches the operator's mental model ("the player
// pays the number on the screen"). The `taxRate` field is informational:
// it's the rate that applies for factura back-out (gestor needs the
// pre-tax base for the 303/130 quarterly filings); it does NOT multiply
// the line during normal money math.
//
// Totals are derived, never stored on the booking:
//   lineGross = quantity * unitPrice          — what the player pays for the line
//   totalDue  = sum(lineGross)                — authoritative booking price (tax-inclusive)
//   subtotal  = sum(lineGross / (1 + taxRate)) — pre-tax base, for factura
//   taxTotal  = totalDue - subtotal            — IVA portion, for factura
//
// `paymentAmount` on the booking is the canonical tax-inclusive scalar
// (matches what the player pays); `lineItems` is the line-by-line
// breakdown. Both are written together — `paymentAmount` for simple
// sum queries, `lineItems` for `totalDue(b)` + asymmetric splits.
//
// `playerIndex` on a line optionally attributes it to a single player (for
// asymmetric splits like a guest_fee on one player only). When undefined, the
// line is split evenly across all players.

export const BOOKING_LINE_ITEM_TYPE = [
  "court_fee",
  "lights",
  "heating",
  "ball_rental",
  "racket_rental",
  "guest_fee",
  "coach_fee",
  "discount",
  "bono_credit",
  "wallet_credit",
  "other",
] as const;
export const BookingLineItemTypeSchema = z.enum(BOOKING_LINE_ITEM_TYPE);
export type BookingLineItemType = z.infer<typeof BookingLineItemTypeSchema>;

export const BOOKING_LINE_ITEM_TYPE_LABELS: Record<BookingLineItemType, string> = {
  court_fee: "Alquiler de pista",
  lights: "Luz",
  heating: "Calefacción",
  ball_rental: "Alquiler de pelotas",
  racket_rental: "Alquiler de pala",
  guest_fee: "Fee de invitado",
  coach_fee: "Honorarios del profesor",
  discount: "Descuento",
  bono_credit: "Bono aplicado",
  wallet_credit: "Monedero aplicado",
  other: "Otro",
};

export const BOOKING_LINE_ITEM_SOURCE = [
  "pricing_rule",
  "manual",
  "auto",
  "coupon",
  "bono",
  "wallet",
  // A product (venta) sold on this booking's tab. `sourceId` carries the
  // catalogue productId. Unified-tab: booking-linked products are line items
  // here, not separate Sale docs — the booking holds the single ledger.
  "sale",
] as const;
export const BookingLineItemSourceSchema = z.enum(BOOKING_LINE_ITEM_SOURCE);
export type BookingLineItemSource = z.infer<typeof BookingLineItemSourceSchema>;

export const BookingLineItemSchema = z.object({
  id: z.string(),
  // Lenient — unknown line-item types coerce to `'other'` so the line
  // still renders with its label/price instead of dropping the whole
  // booking from the UI. Forms with strict input should import
  // `BookingLineItemTypeSchema` directly.
  type: lenient(BookingLineItemTypeSchema, 'other', 'lineItem.type'),
  label: z.string(),
  quantity: z.number().int().positive(),
  unitPrice: z.number(),
  /** 0.0–1.0 inclusive. 0 = exempt. */
  taxRate: z.number().min(0).max(1),
  /** Optional attribution to a single player (index into playerNames).
   *  Undefined = line shared evenly across all players. */
  playerIndex: z.number().int().nonnegative().nullable().optional(),
  source: lenientNullable(BookingLineItemSourceSchema, 'lineItem.source'),
  sourceId: z.string().nullable().optional(),
  addedAt: timestampToDate,
  addedByUid: z.string().nullable().optional(),
});
export type BookingLineItem = z.infer<typeof BookingLineItemSchema>;

export const BOOKING_SOURCE = ["manual", "online", "usersmatch"] as const;
export const BookingSourceSchema = z.enum(BOOKING_SOURCE);
export type BookingSource = z.infer<typeof BookingSourceSchema>;

/**
 * What this booking is. Drives UI iconography, KPI exclusions (e.g. `block`
 * must not count toward occupancy or revenue) and permission nuances.
 *
 * Values:
 * match      — standard customer booking (default)
 * class      — class / clinic with a coach (may have students on the roster)
 * tournament — tournament round; excluded from regular revenue reporting
 * block      — maintenance / closure / weather block; not a real booking
 */
export const BOOKING_TYPE = ["match", "class", "tournament", "block"] as const;
export const BookingTypeSchema = z.enum(BOOKING_TYPE);
export type BookingType = z.infer<typeof BookingTypeSchema>;

export const BOOKING_TYPE_LABELS: Record<BookingType, string> = {
  match: "Partido",
  class: "Clase",
  tournament: "Torneo",
  block: "Bloqueo",
};

/**
 * Customer kind — the CRM identity that owns a booking.
 *
 * - `person`    — a CrmPlayer with kind=person (the typical case)
 * - `group`     — a CrmPlayer with kind=group (e.g. "Equipo López", "Clase 18h")
 * - `anonymous` — no CRM link; walk-in / cash booking with optional roster
 *
 * See `docs/booking-3-slot-customer.md` for the full mental model.
 */
export const CUSTOMER_KIND = ["person", "group", "anonymous"] as const;
export const CustomerKindSchema = z.enum(CUSTOMER_KIND);
export type CustomerKind = (typeof CUSTOMER_KIND)[number];

/**
 * Customer role on the booking.
 *
 * - `plays`        — Customer is on the roster (default for person/match bookings)
 * - `booker-only`  — Customer placed the reservation but is not on the roster
 *                    (Carmen books for Antonio + 3 friends)
 *
 * Null for anonymous bookings or groups (groups don't "play"; anonymous has no CRM).
 */
export const CUSTOMER_ROLE = ["plays", "booker-only"] as const;
export const CustomerRoleSchema = z.enum(CUSTOMER_ROLE);
export type CustomerRole = (typeof CUSTOMER_ROLE)[number];

export const BookingSchema = z
  .object({
      id: z.string(),
      clubId: z.string().default(""),
      courtId: z.string().default(""),
      /** denormalized — cascaded on court rename. */
      courtName: z.string().default(""),
      /** Date key for day-based queries: 'yyyy-MM-dd'. */
      dateKey: z.string().default(""),
      startDateTime: timestampToDate,
      durationMinutes: z.number().int().default(90),
      playerNames: z.array(z.string()).default([]),
      playerPhone: z.string().nullable().optional(),
      playerEmail: z.string().nullable().optional(),
      /**
       * Denormalized booker display name. Set when `customerRole` is
       * `booker-only` (Carmen reserves for Antonio + 3) — mirrors the
       * linked `bookerCrmId` profile's name so list/calendar rows can
       * render "Antonio · Carmen reservó" without resolving the CRM
       * player. Cascaded on CRM rename. Null otherwise.
       */
      bookerName: z.string().nullable().optional(),
      notes: z.string().nullable().optional(),
      status: BookingStatusSchema.default("confirmed"),
      paymentStatus: PaymentStatusSchema.default("pending"),
      /** Pre-tax court-fee scalar — canonical price the operator typed in. */
      paymentAmount: z.number().nullable().optional(),
      /**
       * Ordered priced lines composing the booking total (court fee + surcharges
       * + add-ons + credits). Always carries one `court_fee` line for any
       * priced booking; empty for free / no-price bookings.
       */
      lineItems: z.array(BookingLineItemSchema).default([]),
      /** Payment events log — each entry is one collection or refund. */
      payments: z.array(PaymentSchema).default([]),
      // Lenient on the cosmetic categorical enums below. Drift on these
      // shouldn't hide a real booking — the operator still sees row +
      // amount + player. The wrapper combines `.catch(fallback)` with a
      // drift-log so Sentry still hears about it. `.default()` covers
      // the separate missing-field case. Strict validation lives on forms.
      source: lenient(BookingSourceSchema, 'manual', 'booking.source').default('manual'),
      /** What this booking represents (see `BookingType`). */
      bookingType: lenient(BookingTypeSchema, 'match', 'booking.bookingType').default('match'),
      /**
       * When true, this booking slot is open for other players to join (open-match
       * feature, roadmap #123). Queryable so reception can list "open slots today".
       * Refinement: when true, `playerNames.length` must not exceed the padel
       * maximum of 4. The precise per-court cap (normal=4, small=2) is enforced
       * at the domain/repository level where the court document is available.
       */
      isOpenMatch: z.boolean().default(false),
      createdByUid: z.string().default(""),
      /** frozen — staff display name at booking creation for audit. */
      createdByName: z.string().nullable().optional(),
      lastModifiedByUid: z.string().nullable().optional(),
      /** frozen — staff display name at last edit. */
      lastModifiedByName: z.string().nullable().optional(),
      lastModifiedAt: timestampToDateNullable.nullable().optional(),
      cancelledAt: timestampToDateNullable.nullable().optional(),
      cancellationReason: z.string().nullable().optional(),
      isRecurring: z.boolean().default(false),
      recurringGroupId: z.string().nullable().optional(),
      isException: z.boolean().default(false),
      // ── tournament court hold (spec/19 BR-10d) ───────────────────────────
      /**
       * The tournament this booking holds a court for, and the exact match it
       * belongs to. Both null on every ordinary booking.
       *
       * Same shape as `recurringGroupId` above: a nullable back-reference that
       * lets the owning feature find its own rows without a separate index or
       * a parallel collection. `tournamentMatchId` is what makes reconciliation
       * safe — the reconciler only ever touches bookings carrying an id that
       * belongs to the tournament it is reconciling, so it can never delete a
       * walk-in that happens to sit in the same slot.
       *
       * A torneo without a start time schedules no matches and therefore holds
       * no courts: both stay null and nothing appears on the calendar.
       */
      tournamentId: z.string().nullable().optional(),
      tournamentMatchId: z.string().nullable().optional(),
      // ── 3-slot customer model (see docs/booking-3-slot-customer.md) ──────
      /** CRM identity that owns this booking. Null for anonymous bookings. */
      customerCrmId: z.string().nullable().optional(),
      /** Discriminator denormalized from the linked CrmPlayer.kind. */
      customerKind: lenient(CustomerKindSchema, 'anonymous', 'booking.customerKind').default('anonymous'),
      /**
       * Set when the customer placed the reservation but is not on the roster
       * (Carmen books for Antonio + 3 friends → `bookerCrmId = Carmen.id`,
       * `customerRole = 'booker-only'`). Otherwise null.
       */
      bookerCrmId: z.string().nullable().optional(),
      /** See `CustomerRole`. Null for anonymous and for groups. */
      customerRole: lenientNullable(CustomerRoleSchema, 'booking.customerRole'),
      /**
       * Per-row CRM links, parallel to `playerNames`. `playerCrmIds[i] = null`
       * means row i is unlinked free text. Length must match `playerNames`.
       */
      playerCrmIds: z.array(z.string().nullable()).default([]),
      /**
       * Denormalized name of the linked Customer (`customerCrmId`'s
       * CrmPlayer). Cascaded on customer rename — same pattern as
       * `courtName`. Null for anonymous bookings. List/calendar label
       * helpers read this so they don't need to fetch CrmPlayer to
       * display "Equipo López" or any group/booker-only case.
       */
      customerName: z.string().nullable().optional(),
      /**
       * The coach teaching this class. Set only on `bookingType: 'class'`
       * bookings — the link between a coach and the classes they teach, and
       * the substrate for the coach agenda lens + per-coach report. Holds the
       * coach's staff `uid` (employed coaches) OR the login-less coach's staff
       * doc id (freelance coaches have no `uid`) — resolve against the club's
       * staff subcollection. Null on match / tournament / block bookings.
       */
      coachUid: z.string().nullable().optional(),
      /**
       * Denormalized coach display name — mirrors the linked coach staff
       * doc's name so calendar/list rows render "Clase · Carlos" without a
       * staff fetch. Cascaded on staff rename. Null when `coachUid` is null.
       */
      coachName: z.string().nullable().optional(),
      isSeeded: z.boolean().default(false),
      createdAt: serverStampDate,
      updatedAt: serverStampDate,
    })
  .superRefine((data, ctx) => {
    // Open-match bookings must not exceed the padel court maximum of 4 players.
    // The per-court capacity (normal=4, small=2) is enforced at booking-creation
    // time in the domain layer where the court document is accessible.
    if (data.isOpenMatch && data.playerNames.length > 4) {
      ctx.addIssue({
        code: "too_big",
        maximum: 4,
        type: "array",
        inclusive: true,
        path: ["playerNames"],
        message: "Open-match bookings cannot have more than 4 players (padel court maximum).",
      });
    }

    // ── 3-slot customer-model invariants (see docs/booking-3-slot-customer.md)
    if (data.customerKind === "anonymous") {
      if (data.customerCrmId != null) {
        ctx.addIssue({
          code: "custom",
          path: ["customerCrmId"],
          message: "Anonymous bookings must not carry a customerCrmId.",
        });
      }
      if (data.bookerCrmId != null) {
        ctx.addIssue({
          code: "custom",
          path: ["bookerCrmId"],
          message: "Anonymous bookings must not carry a bookerCrmId.",
        });
      }
      if (data.customerRole != null) {
        ctx.addIssue({
          code: "custom",
          path: ["customerRole"],
          message: "Anonymous bookings must not set customerRole.",
        });
      }
    } else if (data.customerKind === "person" || data.customerKind === "group") {
      if (data.customerCrmId == null) {
        ctx.addIssue({
          code: "custom",
          path: ["customerCrmId"],
          message: "Non-anonymous bookings require customerCrmId.",
        });
      }
    }
    if (data.customerRole === "plays" && data.customerKind !== "person") {
      ctx.addIssue({
        code: "custom",
        path: ["customerRole"],
        message: "customerRole='plays' is only valid when customerKind='person'.",
      });
    }
    if (data.bookerCrmId != null && data.customerRole !== "booker-only") {
      ctx.addIssue({
        code: "custom",
        path: ["bookerCrmId"],
        message: "bookerCrmId requires customerRole='booker-only'.",
      });
    }
  });

export type Booking = z.infer<typeof BookingSchema>;

// ─── Derived helpers ────────────────────────────────────────────────────────

function pad2(n: number): string {
  return n.toString().padStart(2, "0");
}

/** End DateTime = startDateTime + durationMinutes. */
export function bookingEndDateTime(b: Booking): Date {
  return new Date(b.startDateTime.getTime() + b.durationMinutes * 60_000);
}

/** 'HH:mm' start label. */
export function bookingStartTimeLabel(b: Booking): string {
  return `${pad2(b.startDateTime.getHours())}:${pad2(b.startDateTime.getMinutes())}`;
}

/** 'HH:mm' end label. */
export function bookingEndTimeLabel(b: Booking): string {
  const end = bookingEndDateTime(b);
  return `${pad2(end.getHours())}:${pad2(end.getMinutes())}`;
}

/** Minutes elapsed since startDateTime (negative if not yet started). */
export function bookingMinutesSinceStart(
  b: Booking,
  now: Date = new Date(),
): number {
  return Math.floor((now.getTime() - b.startDateTime.getTime()) / 60_000);
}

/**
 * True when the booking started 15+ minutes ago and has NOT been checked in,
 * cancelled or marked no-show. Used to prompt staff to verify attendance.
 */
export function isBookingOverdue(b: Booking, now: Date = new Date()): boolean {
  if (
    b.status === "checked_in" ||
    b.status === "no_show" ||
    b.status === "cancelled"
  ) {
    return false;
  }
  return bookingMinutesSinceStart(b, now) >= 15;
}

// ─── Line-item derived helpers ─────────────────────────────────────────────

/**
 * Tax-inclusive total for a single line: quantity × unitPrice.
 * `unitPrice` is the final price the player pays per unit (IVA already
 * baked in). Tax math doesn't multiply during normal operations.
 */
export function lineTotal(line: BookingLineItem): number {
  return line.quantity * line.unitPrice;
}

/**
 * Pre-tax subtotal for the booking (factura helper — backs out the
 * IVA portion from each tax-inclusive line). Use `totalDue()` for the
 * customer-facing total; use this only when generating a factura that
 * needs the pre-tax base for the operator's gestor.
 */
export function subtotal(b: Pick<Booking, "lineItems">): number {
  return b.lineItems.reduce((sum, l) => {
    if (l.taxRate <= 0) return sum + lineTotal(l);
    return sum + lineTotal(l) / (1 + l.taxRate);
  }, 0);
}

/**
 * IVA portion of the booking (factura helper — `totalDue − subtotal`).
 * Use only for factura back-out; never to add tax on top of unitPrice.
 */
export function taxTotal(b: Pick<Booking, "lineItems">): number {
  return totalDue({ lineItems: b.lineItems }) - subtotal(b);
}

/**
 * Authoritative booking price — sum of (qty × unitPrice) across all
 * lines. Tax-inclusive (matches the operator's stated intent: the
 * player pays the number on the screen). Falls back to the
 * `paymentAmount` scalar when `lineItems` is empty.
 */
export function totalDue(
  b: Pick<Booking, "lineItems"> & {
    paymentAmount?: number | null | undefined;
  },
): number {
  if (b.lineItems.length > 0) {
    return b.lineItems.reduce((sum, l) => sum + lineTotal(l), 0);
  }
  return b.paymentAmount ?? 0;
}

/** Amount still owed on the booking: `totalDue − paidAmount`. Never negative. */
export function balanceDue(b: Booking): number {
  const due = totalDue(b);
  if (due <= 0) return 0;
  const remaining = due - paidAmount(b);
  return remaining < 0 ? 0 : remaining;
}

/** Pre-tax-inclusive sum of lines with no `playerIndex` (shared across roster). */
function sharedLinesTotal(lineItems: BookingLineItem[]): number {
  return lineItems
    .filter((l) => l.playerIndex == null)
    .reduce((sum, l) => sum + lineTotal(l), 0);
}

/** Tax-inclusive sum of lines attributed to a specific player. */
function attributedLinesTotal(
  lineItems: BookingLineItem[],
  playerIndex: number,
): number {
  return lineItems
    .filter((l) => l.playerIndex === playerIndex)
    .reduce((sum, l) => sum + lineTotal(l), 0);
}

/**
 * Player i's share of the booking total: equal split of shared lines +
 * any lines attributed directly to them. Returns 0 when the roster is empty.
 * This is the single source of truth for asymmetric split payments — the
 * equal-split case falls out when no line carries a `playerIndex`.
 */
export function playerShare(b: Booking, playerIndex: number): number {
  const n = b.playerNames.length;
  if (n === 0) return 0;
  const shared = sharedLinesTotal(b.lineItems) / n;
  const attributed = attributedLinesTotal(b.lineItems, playerIndex);
  return shared + attributed;
}

// ─── Payment-derived helpers ───────────────────────────────────────────────

/** Sum of all payments on the booking. Refunds (negative amounts) subtract. */
export function paidAmount(b: Booking): number {
  return b.payments.reduce((sum, p) => sum + p.amount, 0);
}

/**
 * The moment a booking's revenue was (most recently) collected — the
 * latest positive payment's `paidAt`. `null` when nothing positive has
 * been collected yet (pending / free / fully refunded). Lets the Ingresos
 * ledger offer a payment-time (cash-basis) view alongside booking-time.
 */
export function bookingPaymentDate(b: Booking): Date | null {
  let latest: Date | null = null;
  for (const p of b.payments) {
    if (p.amount > 0 && (latest === null || p.paidAt.getTime() > latest.getTime())) {
      latest = p.paidAt;
    }
  }
  return latest;
}

/** Amount still owed. Zero when fully paid, free, or no price set. */
export function pendingPaymentAmount(b: Booking): number {
  return balanceDue(b);
}

/** Whether a Cobrar action makes sense right now — the single gate for every
 *  collect surface (dashboard "Por cobrar" queue, bookings-page KPI + list-row
 *  Cobrar, payments-page pending counter). Owes money (`pending`/`partial`
 *  balance), is not cancelled (terminal — the cancellation *resolution* is
 *  shown instead, spec 01 BR-32), and is not a `block` (maintenance hold —
 *  never a revenue event, spec 01 BR-3). */
export function isBookingCollectable(b: Booking): boolean {
  if (b.status === 'cancelled') return false;
  if (b.bookingType === 'block') return false;
  // A TOURNAMENT booking is a court hold, not a sale. Its money arrives once,
  // as the event's entry fee (`tournamentPayments` — spec/19), so charging the
  // hold too would take the player's money twice at the desk.
  //
  // It reached here as `pending` for a compounding reason: the planners stored
  // `paymentStatus: 'not_required'`, which is not in PAYMENT_STATUS at all, so
  // it parsed back as the `pending` default — and `bookingPaymentStatus`
  // re-derives from money anyway, where a €0 total is `pending` by definition.
  // Excluding by TYPE is the only thing that holds regardless of what the
  // stored status says (2026-08-02).
  // Guarded on the total, not on the type alone: if a club ever does put a real
  // charge on a tournament booking, that money stays collectable. Only the pure
  // €0 hold is excluded.
  if (b.bookingType === 'tournament' && totalDue(b) <= 0) return false;
  const status = bookingPaymentStatus(b);
  return status === 'pending' || status === 'partial';
}

/** Per-player share for the equal-split case. Null if no players or no price.
 *  For asymmetric splits use `playerShare(b, i)` instead. */
export function perPlayerAmount(b: Booking): number | null {
  const n = b.playerNames.length;
  if (n === 0) return null;
  const due = totalDue(b);
  if (due <= 0) return null;
  return due / n;
}

/** True when any payment was recorded for the given player index. */
export function isPlayerPaid(b: Booking, index: number): boolean {
  return b.payments.some(
    (p) => p.playerIndex === index && p.amount > 0,
  );
}

/** Count of distinct player indices that have at least one positive payment. */
export function bookingPaidPlayerCount(b: Booking): number {
  const set = new Set<number>();
  for (const p of b.payments) {
    if (p.playerIndex != null && p.amount > 0) set.add(p.playerIndex);
  }
  return set.size;
}

/** True when every player on the roster has at least one positive payment. */
export function allPlayersPaid(b: Booking): boolean {
  if (b.playerNames.length === 0) return false;
  for (let i = 0; i < b.playerNames.length; i++) {
    if (!isPlayerPaid(b, i)) return false;
  }
  return true;
}

/** True when payment is non-zero but below the total. */
export function isPartialPayment(b: Booking): boolean {
  const due = totalDue(b);
  const paid = paidAmount(b);
  return due > 0 && paid > 0 && paid + 0.0001 < due;
}

/**
 * Recompute the payment status from the payments log + the booking's price.
 *
 * Rule:
 *   - `free` is the only **sticky** flag — operator-set no-charge, persists.
 *   - balance follows the numbers: `pending` (net ≤ 0), `partial`
 *     (0 < net < total), `paid` (net ≥ total).
 *   - `refunded` is NOT derived. A fully-refunded booking nets to ≤ 0, so it
 *     reads `pending` (owed, collectable) with a separate ↩ refund marker
 *     (`bookingWasRefunded`) — never a terminal-looking "Reembolsada" sitting
 *     next to a live "Cobrar" button. This separates the charge layer (a
 *     refund happened) from the balance layer (what's owed now), matching
 *     Stripe/POS. Sales keep `refunded` because a goods refund closes the
 *     sale — that asymmetry is intentional, not drift.
 *
 * Accepts partial inputs because several call sites (form previews,
 * mutations) synthesize an ad-hoc object. When `lineItems` is provided
 * it drives the total; otherwise the legacy `paymentAmount` scalar.
 */
export function derivePaymentStatus(b: {
  lineItems?: BookingLineItem[] | undefined;
  paymentAmount?: number | null | undefined;
  payments: Payment[];
  paymentStatus?: PaymentStatus | undefined;
}): PaymentStatus {
  if (b.paymentStatus === "free") return "free";
  const total =
    b.lineItems && b.lineItems.length > 0
      ? totalDue({ lineItems: b.lineItems, paymentAmount: b.paymentAmount })
      : (b.paymentAmount ?? 0);
  if (total <= 0) return "pending";
  const paid = b.payments.reduce((s, p) => s + p.amount, 0);
  // Honest BALANCE status. A fully-refunded booking (net ≤ 0) is collectable
  // again, so it reads `pending` — never a terminal-looking `refunded`. The
  // "money was returned" fact is surfaced separately by `bookingWasRefunded`
  // (a ↩ marker), mirroring how Stripe/POS keep the charge layer (refund flag)
  // distinct from the balance layer (open/partial/paid). `refunded` is no
  // longer derived for bookings — retained in PAYMENT_STATUS only so legacy
  // stored docs parse; the UI always re-derives, so it never surfaces. Sales
  // differ on purpose: a goods refund closes the sale, so `deriveSaleStatus`
  // keeps `refunded`.
  if (paid <= 0) return "pending";
  if (paid + 0.0001 < total) return "partial";
  return "paid";
}

/**
 * True when the booking's ledger holds a refund (negative) entry. Drives the
 * ↩ "se reembolsó" marker. Independent of the balance status: a booking can be
 * `pending` (owed, collectable) AND carry a refund in its history — that's the
 * exact case that used to read as a contradictory "Reembolsada + Cobrar".
 */
export function bookingWasRefunded(b: { payments: Payment[] }): boolean {
  return b.payments.some((p) => p.amount < 0);
}

/**
 * What happened to the money on a **cancelled** booking. A cancelled booking
 * has no live balance, so its payment cell must read this *resolution* —
 * `retained` (money kept, e.g. a late-cancel penalty), `refunded` (returned to
 * the player), or `none` (nothing ever changed hands) — and NEVER a
 * `pending`/`partial` balance state, which would render the nonsense
 * "Cancelada · Pendiente 37,50" next to a (correctly hidden) Cobrar action.
 *
 * Single source of truth for every cancelled-booking payment surface — the
 * Reservas list tag, the calendar tile, and the detail panel all derive from
 * this so they cannot drift (the F1/F2 bug: the tile showed "Parcial · €X"
 * while the list showed "Cobrado"). See spec/17-use-cases.md UC-D6.
 */
export type CancelledPaymentResolution = "retained" | "refunded" | "none";
export function cancelledPaymentResolution(b: {
  payments: Payment[];
}): CancelledPaymentResolution {
  const net = b.payments.reduce((s, p) => s + p.amount, 0);
  if (net > 0.0001) return "retained";
  if (bookingWasRefunded(b)) return "refunded";
  return "none";
}

/**
 * Computed payment status (C, `booking-unified-tab.md`). Derive the
 * derivable states (pending/partial/paid/refunded) from `lineItems`+`payments`
 * instead of trusting a stored field — this kills the stored-derived-state
 * drift class (`dce78ac5`). `'free'` is operator intent and is preserved.
 * Read this everywhere instead of `b.paymentStatus`.
 */
export function bookingPaymentStatus(
  b: Pick<Booking, "lineItems" | "payments" | "paymentStatus" | "paymentAmount">,
): PaymentStatus {
  if (b.paymentStatus === "free") return "free";
  // Pass `paymentAmount` too: `derivePaymentStatus` falls back to the scalar
  // when a booking has no lineItems yet (pre-#116-backfill / scalar-only
  // bookings). This is why the scalar can only be dropped (B) once every
  // booking carries a court_fee line.
  return derivePaymentStatus({
    lineItems: b.lineItems,
    paymentAmount: b.paymentAmount,
    payments: b.payments,
  });
}

/**
 * Payment status for LIST FILTERING / counting — like `bookingPaymentStatus`,
 * but a CANCELLED booking resolves to its `cancelledPaymentResolution` (the
 * same primitive the row's PagoTag displays) so it never falls into a live-owed
 * bucket. A cancelled booking has no live balance, so it must NOT appear under
 * the "Pendiente"/"Parcial" payment filters (UC-D6 / F4): retained money reads
 * as `paid` (Cobrado), a refund as `refunded`, and a never-paid cancellation as
 * `null` (matches no payment chip — find it via the Cancelada status filter).
 */
export function bookingPaymentFilterStatus(
  b: Pick<
    Booking,
    "lineItems" | "payments" | "paymentStatus" | "paymentAmount" | "status" | "bookingType"
  >,
): PaymentStatus | null {
  if (b.status === "cancelled") {
    const res = cancelledPaymentResolution(b);
    return res === "retained" ? "paid" : res === "refunded" ? "refunded" : null;
  }
  // Tournament holds carry no collectable balance (see `isBookingCollectable`),
  // so they must not fall into the Pendiente / Parcial buckets — they were
  // inflating every "por cobrar" count the club looks at (2026-08-02).
  if (b.bookingType === "tournament" && totalDue(b) <= 0) return null;
  return bookingPaymentStatus(b);
}

/**
 * The court price = the `court_fee` line's unit price (0 if none). Replaces
 * direct reads of the deprecated `paymentAmount` scalar (B). `lineItems` is the
 * single source of truth for the court price.
 */
export function courtPrice(b: Pick<Booking, "lineItems">): number {
  return b.lineItems.find((l) => l.type === "court_fee")?.unitPrice ?? 0;
}

/**
 * Summarise the mix of payment methods. Returns the single method when all
 * positive payments used the same, `"mixed"` when multiple methods were used,
 * or `null` when there are no payments yet.
 */
export function paymentMethodSummary(
  b: Booking,
): PaymentMethod | "mixed" | null {
  const methods = new Set<PaymentMethod>();
  for (const p of b.payments) {
    if (p.amount > 0) methods.add(p.method);
  }
  if (methods.size === 0) return null;
  if (methods.size === 1) return [...methods][0] ?? null;
  return "mixed";
}

/**
 * 'yyyy-MM-dd' key for day-based Firestore queries.
 *
 * Optional `timezone` parameter — when supplied, the key is formatted in
 * that IANA zone via `Intl.DateTimeFormat`. Caller should pass
 * `club.timezone` so the dateKey is anchored to the CLUB'S local day, not
 * the browser's. Without this, a London admin editing a Madrid booking
 * created at 00:30 Madrid time (= 22:30 UTC previous day) would write
 * `dateKey="2026-05-11"` while the Madrid receptionist wrote
 * `dateKey="2026-05-12"` — off-by-one collisions.
 *
 * Default falls back to the runtime local zone for backwards-compat
 * (Spain Phase-1 single-country code paths still work the same).
 */
export function bookingDateKeyFor(date: Date, timezone?: string): string {
  if (timezone) {
    // `en-CA` locale + 2-digit components emits the ISO-like 'YYYY-MM-DD'
    // shape natively, avoiding manual reordering of month/day from other
    // locales. Use `formatToParts` rather than the raw `format()` output
    // to guarantee the field order regardless of future locale changes.
    const parts = new Intl.DateTimeFormat("en-CA", {
      timeZone: timezone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).formatToParts(date);
    const y = parts.find((p) => p.type === "year")?.value ?? "0000";
    const m = parts.find((p) => p.type === "month")?.value ?? "01";
    const d = parts.find((p) => p.type === "day")?.value ?? "01";
    return `${y}-${m}-${d}`;
  }
  return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;
}
